import { ButtonStyle, GradientButtonTheme, SiteCorebuttonData } from './button.types';

const GRADIENT_BUTTON_RADIUS = '34.27px 35px 2.86px 34.27px';
/** Keyboard focus shape (bottom-right ≈ standard/primary token). */
const GRADIENT_FOCUS_RADIUS = '30px 37px 4px 30px';
/** Outer stroke (black) — same width as default transparent border to avoid layout shift. */
const GRADIENT_FOCUS_OUTER_COLOR = 'var(--button-color-outline, #001433)';
/** White band *inside* the black border, between border and gradient (Figma double ring). */
const GRADIENT_FOCUS_INNER_HALO = 'inset 0 0 0 1px #FFFFFF';

function capitalizeFirstLetter(val?: string) {
  if (!val) '';
  return String(val).charAt(0).toUpperCase() + String(val).slice(1).toLowerCase();
}

function generateButtonStyles(data: SiteCorebuttonData): ButtonStyle | null {
  if (!data) return null;
  return {
    height: data?.buttonHeight?.value,
    background: {
      default: data?.enabledBackgroundColor?.value,
      hover: data?.hoverBackgroundColor?.value,
      disabled: data?.disabledBackgroundColor?.value,
    },
    borderRadius: data?.buttonBorderRadius?.value,
    textColor: {
      hover: data?.hoverTextColor?.value,
      default: data?.enabledTextColor?.value,
      disabled: data?.disabledTextColor?.value,
      pressed: data?.pressedTextColor?.value,
    },
    borderColor: {
      hover: data?.hoverBorderColor?.value,
      default: data?.enabledBorderColor?.value,
      disabled: data?.disabledBorderColor?.value || data?.enabledBorderColor?.value + '7A',
    },
    focusedBorderColor: data?.focusedBorderColor?.value,
  };
}

type ButtonStylesCustom = {
  disabledOpacity?: string;
};

function applyButtonStyles(
  data?: ReturnType<typeof generateButtonStyles>,
  options?: ButtonStylesCustom
) {
  if (!data) null;
  const enableBorder = !!data?.borderColor?.default && data?.borderColor?.default !== 'unset';

  return {
    fontSize: '16px',
    borderRadius: data?.borderRadius,
    background: data?.background.default,
    color: data?.textColor.default,
    borderWidth: enableBorder ? '1px' : 0,
    borderColor: data?.borderColor?.default,
    borderStyle: 'solid',
    '&:hover': {
      background: data?.background.hover,
      color: data?.textColor.hover,
      borderColor: data?.borderColor?.hover,
    },
    '&:disabled': {
      background: data?.background.disabled,
      color: data?.textColor.disabled,
      opacity: options?.disabledOpacity || 1,
      borderColor: data?.borderColor?.disabled,
    },
  };
}

/** Figma: typography/font/size xl + lineHeight xl + spacing/letter xl (gradient CTA only). */
const GRADIENT_LABEL_FONT_SIZE = '20px';
const GRADIENT_LABEL_LINE_HEIGHT = '30px';
const GRADIENT_LABEL_LETTER_SPACING = '0.4px';

function applyGradientButtonStyles(
  data: GradientButtonTheme,
  options: {
    /** Default label color (primary `enabledTextColor`). */
    textColor: string;
    /** Hover label color (primary `hoverTextColor`). */
    textColorHover: string;
    /** Pressed/active label color (primary `pressedTextColor`). */
    textColorPressed: string;
    hasStartIcon?: boolean;
    /** typography/font/family (primary body stack). */
    fontFamily: string;
  }
) {
  const { textColor, textColorHover, textColorPressed, hasStartIcon, fontFamily } = options;
  return {
    //width: 'auto',
    minWidth: 196,
    minHeight: 50,
    height: 50,
    padding: '1px 24px 1px 1px',
    boxSizing: 'border-box' as const,
    // 2px so focus can swap to black without jumping; white halo is inset on top of gradient.
    border: '2px solid transparent',
    borderRadius: GRADIENT_BUTTON_RADIUS,
    background: data.background.default,
    color: textColor,
    fontFamily,
    fontSize: GRADIENT_LABEL_FONT_SIZE,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: GRADIENT_LABEL_LINE_HEIGHT,
    letterSpacing: GRADIENT_LABEL_LETTER_SPACING,
    textAlign: 'center' as const,
    verticalAlign: 'middle',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    boxShadow: '0px 5.5px 6px -2px rgba(0, 190, 177, 0.1)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: hasStartIcon ? 'flex-start' : 'center',
    // Spacing between leading image and label (Figma auto-layout); gap on Button should match.
    gap: hasStartIcon ? '8px' : undefined,
    '& .MuiButton-startIcon': {
      // MUI defaults: marginLeft -4, marginRight 8 — breaks gradient pill layout; gap handles spacing.
      margin: 0,
      marginLeft: 0,
      marginRight: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
      flexShrink: 0,
      lineHeight: 0,
      height: '100%',
      minHeight: 50,
      // MUI sets fontSize 18–22px on first child (icon slot); reset so the CTA image keeps intrinsic size.
      '& > *:nth-of-type(1)': {
        fontSize: 0,
        lineHeight: 0,
      },
    },
    '&:hover': {
      background: data.background.hover,
      color: textColorHover,
      boxShadow: '0px 5.5px 6px -2px rgba(0, 115, 107, 0.1)',
    },
    '&:active': {
      background: data.background.pressed,
      color: textColorPressed,
    },
    '&:disabled': {
      opacity: 0.5,
      color: textColor,
    },

    '&.Mui-focusVisible': {
      position: 'relative',
      borderRadius: GRADIENT_FOCUS_RADIUS,
      borderColor: GRADIENT_FOCUS_OUTER_COLOR,
      outline: 'none',
      boxShadow: `0px 5.5px 6px -2px rgba(0, 190, 177, 0.1), ${GRADIENT_FOCUS_INNER_HALO}`,
    },
  };
}

export {
  capitalizeFirstLetter,
  generateButtonStyles,
  applyButtonStyles,
  applyGradientButtonStyles,
};
